/**
 * MessageFileDropZone.js
 * Talkroute
 *
 * Created by Vel Genov on 10/14/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */
/* eslint react/prop-types: 0 */
import React from "react";
import Jimp from "jimp";
import { connect } from "react-redux";
import Dropzone, { DropzoneRef } from "react-dropzone";
import { withToastManager } from "react-toast-notifications";
import { Button } from "semantic-ui-react";

import appConfig from "../../config/index";

import { updateFiles } from "../../actions/textMessageDraftActions";
import uiString from "../../constants/uiString";
import imageMIME from "../../constants/imageMIME";
import validateArray from "../../helpers/arrayValidator";
import FilePathHelper, { localFilePaths } from "../../helpers/filePathHelper";
import { ATTACHMENT_SIZE } from "../../services/dataClasses/TextMessageAttachment";
import apiConstants from "../../constants/apiConstants";
import AttachSvg from "@resources/icons/message-input-box-attach.svg";
import path from "path";
import { getTextMessageAllowedForNumber } from "@services/dataClasses/TalkroutePhoneNumber";
import { NEW_CONVERSATION_ID } from "../../services/dataClasses/ConversationData";

interface IMessageFileDropZoneProps {
  pictureLoadingCallback: (loaded: boolean) => any;
  children: any;
  updateFiles: (files: any[]) => any;
  openFileTriggerCallback: (trigger: any) => any;
  toastManager: any;
  conversations: any[];
  outgoingPhoneNumbers?: any[];
  currentConversationId?: string;
}

/**
 * Creates a file drop zone around its children elements
 */
class MessageFileDropZone extends React.Component<
  IMessageFileDropZoneProps,
  any
> {
  static defaultProps = {
    conversations: [],
    outgoingPhoneNumbers: [],
  };

  path = appConfig.path!;
  onDrop = (files, rejectedFiles) => {};
  dropZone: DropzoneRef | null = null;

  /**
   * New files added callback
   */
  filesAdded = () => {
    const { files, rejectedFiles } = this.state;
    const { pictureLoadingCallback } = this.props;
    pictureLoadingCallback(true);

    if (validateArray(rejectedFiles)) {
      // There were rejected files
      this.showRejectedFilesToast();
    }

    if (files) {
      const file = files[0];
      if (file) {
        if (file.path) {
          // Give the system 500ms to wrap up before processing the images
          // This should be removed when a non blocking image processing solution is in place
          setTimeout(() => {
            this.processNewFile(file);
          }, 500);
        }
      }
    }
  };

  /**
   * Display a toast that the files added were rejected
   */
  showRejectedFilesToast = (message?) => {
    const { toastManager, pictureLoadingCallback } = this.props;
    pictureLoadingCallback(false);

    let updatedMessage = uiString.UNSUPPORTED_ATTACHMENT_TYPE;

    if (message) {
      updatedMessage = message;
    }

    toastManager.add(<div>{updatedMessage}</div>, {
      appearance: "error",
      autoDismiss: true,
      pauseOnHover: false,
    });
  };

  /**
   * Processes a new image file
   *
   * @param file the file information
   */
  processNewFile = (file) => {
    const { path: filePath } = file;
    try {
      const fileNameWithExtension = this.path.basename(filePath);
      const fileExtension = this.path.parse(fileNameWithExtension).ext;

      switch (fileExtension.toLowerCase()) {
        case ".gif":
          this.processGIF(file);
          return;
        default:
          this.processSupportedImageFiles(file);
        // this.processImageElectronNative(filePath);
      }
    } catch (error) {
      if (error) {
        const { name, message } = error as any;
        if (name && message) {
          this.showRejectedFilesToast(`${name} ${message}`);
        }

        return error;
      }
      this.showRejectedFilesToast(uiString.FILE_TOO_LARGE);
    }
  };

  /**
   * Resize the image with Electron nativeImage
   * Supported file formats JPEG and PNG
   *
   * @returns a resized image
   */
  resizeImage = (width, height, image) => {

    if (width > ATTACHMENT_SIZE.width || height > ATTACHMENT_SIZE.height) {
      // The image needs resizing
      // Calculate the width and height ratios
      const widthRatio = ATTACHMENT_SIZE.width / width;
      const heightRatio = ATTACHMENT_SIZE.height / height;
      let newWidth;
      let newHeight;

      if (widthRatio > heightRatio) {
        // Use the lower ratio
        newWidth = width * heightRatio;
        newHeight = height * heightRatio;
      } else {
        newWidth = width * widthRatio;
        newHeight = height * widthRatio;
      }

      const resizedImage = image.resize({ width: newWidth, height: newHeight }); // Resize

      return resizedImage;
    }

    // No resize necessary
    return image;
  };

  getIsEditable = () => {
    const { conversations, currentConversationId, outgoingPhoneNumbers } = this.props;

    if (currentConversationId === NEW_CONVERSATION_ID) {
      return true;
    }

    let currentPhoneNumber = ""; 
  
    if (conversations && currentConversationId) {
      const currentConversation = conversations.find(
        (conversation) => conversation.conversationId === currentConversationId
      );
      if (currentConversation) {
        currentPhoneNumber = currentConversation.talkrouteNumber; 
      }
    }
  
    return getTextMessageAllowedForNumber(currentPhoneNumber, outgoingPhoneNumbers);
  };

  /**
   * Process the image with Electron nativeImage
   * Supported file formats JPEG and PNG
   */
  /*
  processImageElectronNative = (filePath) => {
    try {
      const fileNameWithExtension = path.basename(filePath);
      const fileName = path.parse(fileNameWithExtension).name;
      const fileExtension = path.parse(fileNameWithExtension).ext;
      // Create a temp file file name
      const updatedFileExtension = '.jpeg'; // Convert the new file to JPEG
      const updatedFileNameWithExtension = fileName + updatedFileExtension;
      const tempDataPath = path.join(filePathHelper.userDataPath, localFilePaths.TEMP);
      const fullImagePath = path.join(
        tempDataPath,
        updatedFileNameWithExtension
      );

      if (!appConfig.fileStorage.exists(tempDataPath)) {
        appConfig.fileStorage.createPath(tempDataPath);
      }

      // Open the image
      const newImage = nativeImage.createFromPath(filePath);
      const { width, height } = newImage.getSize();

      if (width === 0 || height === 0) {
        // If the image is empty, there was a problem opening the file
        // Most probably not a supported file type or a broken file
        this.showRejectedFilesToast(uiString.UNSUPPORTED_ATTACHMENT_TYPE);

        return;
      }

      const resizedImage = this.resizeImage(width, height, newImage);

      const jpgImage = resizedImage.toJPEG(ATTACHMENT_SIZE.jpgQuality);

      appConfig.fileStorage.save(fullImagePath, jpgImage);

      // Verify the file size meets the requirements
      const bitmap = appConfig.fileStorage.get(fullImagePath);
      const buffer = Buffer.from(bitmap);
      const { byteLength } = buffer;
      if (
        byteLength >
        apiConstants().textMessageAttachmentSettings.MAX_ATTACHMENT_SIZE
      ) {
        this.showRejectedFilesToast(uiString.FILE_TOO_LARGE);
      } else {
        this.updateDataModelWithFiles(fullImagePath);
      }
      return fullImagePath;
    } catch (error) {
      if (error) {
        const { name, message } = error;
        if (name && message) {
          this.showRejectedFilesToast(`${name} ${message}`);
        }

        return error;
      }

      this.showRejectedFilesToast(uiString.FILE_TOO_LARGE);
    }
  };
  */

  /**
   * Process a gif file
   * If the file is over MAX_ATTACHMENT_SIZE (currently 1MB), an error is shown
   *
   * @param file file information
   */
  processGIF = async (file) => {
    const { path: filePath } = file;
    const isAbsolutePath = path.isAbsolute(filePath);

    if (isAbsolutePath) {
      const bitmap = appConfig.fileStorage!.get(filePath);
      const buffer = Buffer.from(bitmap);
      this.onImageLoadedValidation(buffer, filePath);
    } else {
      const imageBase64 = await this.fileReaderBase64OnloadPromise(file);
      const imageBuffer = await this.fileReaderBufferOnloadPromise(file);
      this.onImageLoadedValidation(imageBuffer, imageBase64);
    }
  };

  /**
   * Process all supported files with Jimp
   */
  processSupportedImageFiles = async (file) => {

    const { path: filePath } = file;
    const isAbsolutePath = path.isAbsolute(filePath);
    let jimpPromise;
    if (isAbsolutePath) {
      jimpPromise = Jimp.read(filePath);
    } else {
      const fileReaderResult: any = await this.fileReaderBase64OnloadPromise(file)
      jimpPromise = Jimp.read(fileReaderResult);
    }
    jimpPromise
      .then(async (image) => {

        const { width, height } = image.bitmap;
        if (width > ATTACHMENT_SIZE.width || height > ATTACHMENT_SIZE.height) {
          // The image needs resizing
          // Calculate the width and height ratios
          const widthRatio = ATTACHMENT_SIZE.width / width;
          const heightRatio = ATTACHMENT_SIZE.height / height;
          let newWidth;
          let newHeight;

          if (widthRatio > heightRatio) {
            // Use the lower ratio
            newWidth = width * heightRatio;
            newHeight = height * heightRatio;
          } else {
            newWidth = width * widthRatio;
            newHeight = height * widthRatio;
          }

          image.resize(newWidth, newHeight); // Resize
          image.quality(ATTACHMENT_SIZE.jpgQuality); // Set JPEG quality
        }

        if (isAbsolutePath) {
          // Write file to temp directory
          const fileNameWithExtension = this.path.basename(filePath);
          const fileName = this.path.parse(fileNameWithExtension).name;
          const updatedFileExtension = ".jpeg"; // Convert the new file to JPEG
          const updatedFileNameWithExtension = fileName + updatedFileExtension;
          const fullImagePath = this.path.join(
            new FilePathHelper().userDataPath,
            localFilePaths.TEMP,
            updatedFileNameWithExtension
          );

          image.writeAsync(fullImagePath).then(() => {
            const bitmap = appConfig.fileStorage!.get(fullImagePath);
            const buffer = Buffer.from(bitmap);
            return this.onImageLoadedValidation(buffer, fullImagePath);
          });
        } else {
          const imageBase64 = await image.getBase64Async(file.type);
          image
            .getBufferAsync(file.type)
            .then((buffer) => {
              return this.onImageLoadedValidation(buffer, imageBase64);
            })
            .catch((error) => {
              this.processJimpError(error);
            });
        }

        return image;
      })
      .catch((error) => {
        this.processJimpError(error);
      });
  };

  /**
   * Return reader.onload as buffer
   *
   * @param file file information
   */
  private fileReaderBase64OnloadPromise(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (e: any) => {
        const content: any = reader.result;
        resolve(content);
      };
    });
  }

  /**
   * Return reader.onload as base64
   *
   * @param file file information
   */
  private fileReaderBufferOnloadPromise(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (e: any) => {
        const content: any = reader.result;
        resolve(content);
      };
    });
  }

  /**
   * Validate image properties
   *
   * @param buffer the error to parse
   * @param image the error to parse
   */
  onImageLoadedValidation = (buffer, image) => {
    const { byteLength } = buffer;
    if (
      byteLength >
      apiConstants().textMessageAttachmentSettings.MAX_ATTACHMENT_SIZE
    ) {
      this.showRejectedFilesToast(uiString.FILE_TOO_LARGE);
    } else {
      this.updateDataModelWithFiles(image);
    }
  };

  /**
   * Parse and display an error coming from the Jimp image processing
   *
   * @param error the error to parse
   */
  processJimpError = (error: any) => {
    if (error) {
      const { name, message } = error;
      if (name && message) {
        this.showRejectedFilesToast(`${name} ${message}`);
      }

      return error;
    }

    this.showRejectedFilesToast(uiString.FILE_TOO_LARGE);
  };

  /**
   * Add the newly added files to the data model
   */
  updateDataModelWithFiles = (files) => {
    const {
      updateFiles: updateFilesLocal,
      pictureLoadingCallback,
    } = this.props;
    pictureLoadingCallback(false);

    updateFilesLocal(files);
  };

  /**
   * Create an open file trigger
   *
   * @returns a JSX of the file open trigger
   */
  setOpenFileTrigger = () => {
    const isEditable = this.getIsEditable();
    const openFileTrigger = (
      <Button
        basic
        icon
        compact
        style={{
          boxShadow: "none",
          paddingLeft: "0px",
          paddingRight: "0px",
        }}
        onClick={this.openFileDialog}
        disabled={!isEditable}
      >
        <AttachSvg />
      </Button>
    );

    const { openFileTriggerCallback } = this.props;
    if (openFileTriggerCallback) {
      openFileTriggerCallback(openFileTrigger);
    }
  };

  /**
   * A request to open the open file dialog
   */
  openFileDialog = () => {
    const { dropZone } = this;
    const isEditable = this.getIsEditable();

    if (!isEditable) {
      return;
    }

    if (dropZone) {
      dropZone.open();
    }
  };

  constructor(props) {
    super(props);
    this.onDrop = (files, rejectedFiles) => {
      this.setState({ files, rejectedFiles });
      this.filesAdded();
    };
    this.state = {
      files: [],
    };
  }

  componentDidMount() {
    this.setOpenFileTrigger();
  }

  componentDidUpdate(prevProps) {
    if (this.props.currentConversationId !== prevProps.currentConversationId ||
      this.props.conversations !== prevProps.conversations ||
      this.props.outgoingPhoneNumbers !== prevProps.outgoingPhoneNumbers) {
      this.setOpenFileTrigger();
    }
  }

  render() {
    const { children } = this.props;
    const conversationView = (
      <Dropzone
        onDrop={this.onDrop}
        ref={(dropZone) => {
          this.dropZone = dropZone;
        }}
        noClick
        noKeyboard
        accept={`${imageMIME.GIF}, ${imageMIME.PNG}, ${imageMIME.JPEG}, ${imageMIME.BMP}, ${imageMIME.TIFF}`}
      >
        {({ getRootProps, getInputProps }) => (
          <div {...getRootProps({ className: "dropzone" })}>
            <input {...getInputProps()} />
            {children}
          </div>
        )}
      </Dropzone>
    );

    return conversationView;
  }
}

/**
 * Map props to a redux state
 * @param {*} state
 */
const mapStateToProps = (state) => ({
  currentConversationId: state.conversations.selectedConversationId,
  conversations: state.conversations.data,
  outgoingPhoneNumbers: state.preferences.outgoingTextPhoneNumbers,
});

export default withToastManager(
  connect(mapStateToProps, { updateFiles })(MessageFileDropZone)
);
